import { useMemo, type ReactNode } from "react";

import { DateTime } from "luxon";
import {
  BanknotesIcon,
  CameraIcon,
  ClockIcon,
  CurrencyPoundIcon,
  ShoppingCartIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import PriceFormat from "../../utils/priceFormat";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { IVehicle } from "../../models/Vehicle";
import { ILead } from "../../models/Lead";

interface VehicleHeaderProps {
  vehicle: IVehicle;
  lead: ILead | null | undefined;
  /** Parts / priority / yard location & status dropdowns — pinned to the top
   *  of the header card so operators don’t scroll past the hero to change
   *  them. */
  locationControls?: ReactNode;
  /** Primary actions (e.g. View Lead, Delete) — right-aligned on wide
   *  viewports beside the location controls. */
  headerActions?: ReactNode;
}

// Reuses the visual grammar from `StockHeader` so the vehicle page and the
// parts stock page feel like the same product: hero image, title + plate/VIN
// chips, colour-coded lifecycle badges, and a headline price on the right.
// Everything here is sourced from `IVehicle` / `ILead` fields already in the
// partial DTOs — no extra requests.
export default function VehicleHeader({
  vehicle,
  lead,
  locationControls,
  headerActions,
}: VehicleHeaderProps) {
  const thumbUrl = vehicle.vehicleImageUrl || null;

  const titleBits = [
    vehicle.dvlaYearOfManufacture ? String(vehicle.dvlaYearOfManufacture) : null,
    vehicle.combinedMake ?? null,
    vehicle.combinedModel ?? null,
  ].filter(Boolean);
  const title = titleBits.length > 0 ? titleBits.join(" ") : "Vehicle";

  // "In stock" starts at collection; fall back to the record's creation date
  // so older vehicles that were never collected via the app still show a
  // meaningful number rather than a dash.
  const stockStartIso = vehicle.collectedOn ?? vehicle.created ?? null;
  const daysInStock = useMemo(() => {
    if (!stockStartIso) return null;
    const dt = DateTime.fromISO(stockStartIso);
    if (!dt.isValid) return null;
    const diff = Math.floor(DateTime.now().diff(dt, "days").days);
    return diff < 0 ? 0 : diff;
  }, [stockStartIso]);

  const pricePaid = lead?.price ?? null;
  // `IVehicle` doesn't yet expose a realised revenue figure, so the
  // "Total sales" KPI falls back to the projected-sales analytics field
  // (same number powering the Financial Stats tab). Once the backend adds
  // a realised-revenue column we'll swap the source without touching the
  // header's shape.
  const totalSales = vehicle.projectedSales ?? null;
  const totalSold = vehicle.soldPartCount ?? null;
  const partsInStock =
    vehicle.totalPartsInStock ?? vehicle.availablePartCount ?? null;

  return (
    <div className="bg-white shadow rounded-lg p-4 sm:p-6">
      {(locationControls || headerActions) && (
        <div className="mb-4 flex flex-col gap-4 border-b border-slate-200 pb-4 lg:flex-row lg:items-end lg:justify-between lg:gap-6">
          {locationControls && (
            <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 text-sm text-gray-700 sm:grid-cols-2 xl:grid-cols-4">
              {locationControls}
            </div>
          )}
          {headerActions && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {headerActions}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
        <div className="flex items-start gap-3">
          <div
            className={classNames(
              "relative flex-shrink-0 w-32 h-32 md:w-40 md:h-40 bg-gray-50 rounded-md border border-gray-200 overflow-hidden"
            )}
          >
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt={title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex w-full h-full items-center justify-center text-gray-400">
                <CameraIcon className="h-10 w-10" aria-hidden="true" />
              </div>
            )}
            {vehicle.vrm && (
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] font-medium text-white">
                <TruckIcon className="h-3 w-3" aria-hidden="true" />
                {vehicle.vrm}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 truncate">
                {title}
              </h1>

              {/* Identifier chips — VRM and VIN on the same line, side by
                  side, so operators can reach for either without scanning. */}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                {vehicle.vrm && (
                  <span>
                    VRM{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.vrm}
                    </span>
                  </span>
                )}
                {vehicle.stockNumber && (
                  <span>
                    Stock #{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.stockNumber}
                    </span>
                  </span>
                )}
                {(vehicle.vinVerified || vehicle.vinOriginalDVLA) && (
                  <span>
                    VIN{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.vinVerified || vehicle.vinOriginalDVLA}
                    </span>
                  </span>
                )}
                {vehicle.dvlaYearOfManufacture && (
                  <span>
                    Year{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.dvlaYearOfManufacture}
                    </span>
                  </span>
                )}
                {vehicle.combinedMake && (
                  <span>
                    Make{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.combinedMake}
                    </span>
                  </span>
                )}
                {vehicle.combinedModel && (
                  <span>
                    Model{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.combinedModel}
                    </span>
                  </span>
                )}
                {vehicle.colourCurrent && (
                  <span>
                    Colour{" "}
                    <span className="font-medium text-gray-700">
                      {vehicle.colourCurrent}
                    </span>
                  </span>
                )}
              </div>

              {/* Lifecycle badges mirror the StockHeader's "days in stock / on
                  eBay" chips so operators build muscle memory between the
                  two pages. */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {daysInStock !== null && (
                  <InfoBadge
                    icon={
                      <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    }
                    tone="slate"
                    title={`On site since ${formatDateTime(
                      stockStartIso,
                      DateTime.DATE_MED,
                      "-"
                    )}`}
                  >
                    {daysInStock} {daysInStock === 1 ? "day" : "days"} in stock
                  </InfoBadge>
                )}
                {partsInStock !== null && (
                  <InfoBadge
                    icon={
                      <BanknotesIcon
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                      />
                    }
                    tone="blue"
                  >
                    {partsInStock.toLocaleString("en-GB")} parts in stock
                  </InfoBadge>
                )}
                {totalSold !== null && (
                  <InfoBadge
                    icon={
                      <ShoppingCartIcon
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                      />
                    }
                    tone="emerald"
                  >
                    {totalSold.toLocaleString("en-GB")} sold
                  </InfoBadge>
                )}
              </div>
            </div>
          </div>

          {/* Headline numbers — price paid is the primary KPI, total sales
              sits beside it as the secondary metric. */}
          <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Price paid
                </p>
                <p className="mt-0.5 text-3xl font-semibold text-gray-900">
                  {pricePaid !== null ? PriceFormat(pricePaid) : "—"}
                </p>
                {lead?.completedOn && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Purchased{" "}
                    {formatDateTime(lead.completedOn, DateTime.DATE_MED, "")}
                  </p>
                )}
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1">
                  <CurrencyPoundIcon
                    className="h-3.5 w-3.5 text-gray-400"
                    aria-hidden="true"
                  />
                  Total sales
                </p>
                <p className="mt-0.5 text-2xl font-semibold text-emerald-700">
                  {totalSales !== null ? PriceFormat(totalSales) : "—"}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Revenue from this donor
                </p>
              </div>

              {vehicle.recommendedPurchasePrice !== null &&
                vehicle.recommendedPurchasePrice !== undefined && (
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Rec. purchase
                    </p>
                    <p className="mt-0.5 text-xl font-semibold text-primary-700">
                      {PriceFormat(vehicle.recommendedPurchasePrice)}
                    </p>
                  </div>
                )}
            </div>
        </div>
      </div>
    </div>
  );
}

const INFO_BADGE_TONES: Record<string, string> = {
  slate: "bg-slate-50 text-slate-700 ring-slate-200",
  blue: "bg-blue-50 text-blue-700 ring-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

const InfoBadge = ({
  children,
  icon,
  tone = "slate",
  title,
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: keyof typeof INFO_BADGE_TONES;
  title?: string;
}) => (
  <span
    title={title}
    className={classNames(
      "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
      INFO_BADGE_TONES[tone] ?? INFO_BADGE_TONES.slate
    )}
  >
    {icon}
    {children}
  </span>
);
